import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    Optional,
    Self,
    ViewChild,
    ViewEncapsulation,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { ScrollerOptions, TreeNode } from 'primeng/api';
import { TreeFilterEvent, TreeNodeSelectEvent, TreeNodeUnSelectEvent } from 'primeng/tree';
import {
    TreeSelect,
    TreeSelectModule,
    TreeSelectNodeCollapseEvent,
    TreeSelectNodeExpandEvent
} from 'primeng/treeselect';
import { FcpFloatLabelComponent, FloatLabelVariant } from '../fcp-float-label';

/** Spec max overlay width (no semantic size token yet). */
const OVERLAY_MAX_WIDTH_PX = 480;

@Component({
    selector: 'fcp-tree-select',
    imports: [CommonModule, FormsModule, TreeSelectModule, FcpFloatLabelComponent],
    styleUrl: './fcp-tree-select.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()',
        '[class.is-loading]': 'loading()'
    },
    template: `
        <ng-template #selectTemplate>
            <p-treeSelect
                [ngModel]="modelValue()"
                [options]="options()"
                [placeholder]="showFloatLabel() ? '' : placeholder()"
                [disabled]="isDisabled()"
                [selectionMode]="selectionMode()"
                [metaKeySelection]="metaKeySelection()"
                [filter]="filter()"
                [filterPlaceholder]="filterPlaceholder()"
                [filterBy]="filterBy()"
                [filterMode]="filterMode()"
                [filterInputAutoFocus]="filterInputAutoFocus()"
                [resetFilterOnHide]="resetFilterOnHide()"
                [propagateSelectionDown]="propagateSelectionDown()"
                [propagateSelectionUp]="propagateSelectionUp()"
                [showClear]="showClear()"
                [invalid]="invalid()"
                [class.ng-invalid]="isControlInvalidAndDirty"
                [class.ng-dirty]="isControlInvalidAndDirty"
                [variant]="variant()"
                [size]="primeTreeSelectSize()"
                [loading]="loading()"
                [virtualScroll]="virtualScroll()"
                [virtualScrollItemSize]="virtualScrollItemSize()"
                [virtualScrollOptions]="virtualScrollOptions()"
                [scrollHeight]="scrollHeight()"
                [emptyMessage]="emptyMessage()"
                [panelStyle]="resolvedPanelStyle()"
                [panelStyleClass]="resolvedPanelStyleClass()"
                [appendTo]="appendTo()"
                [inputId]="id()"
                [ariaLabel]="ariaLabel()"
                [ariaLabelledBy]="ariaLabelledBy()"
                [fluid]="fluid()"
                [containerStyleClass]="isDisabled() ? 'is-disabled' : ''"
                (ngModelChange)="handleChange($event)"
                (onBlur)="handleBlur()"
                (onFocus)="onFocus.emit($event)"
                (onShow)="handlePanelShow($event)"
                (onHide)="onHide.emit($event)"
                (onClear)="onClear.emit()"
                (onFilter)="handleFilter($event)"
                (onNodeExpand)="handleNodeExpand($event)"
                (onNodeCollapse)="handleNodeCollapse($event)"
                (onNodeSelect)="onNodeSelect.emit($event)"
                (onNodeUnselect)="onNodeUnselect.emit($event)"
            >
                @if (icon()) {
                    <ng-template pTemplate="dropdownicon">
                        <span [class]="icon()!"></span>
                    </ng-template>
                }
            </p-treeSelect>
        </ng-template>

        @if (showFloatLabel()) {
            <fcp-float-label [variant]="treeSelectFloatLabelVariant">
                <ng-container *ngTemplateOutlet="selectTemplate" />
                <label [for]="id()">{{ placeholder() }}</label>
            </fcp-float-label>
        } @else {
            <ng-container *ngTemplateOutlet="selectTemplate" />
        }
    `
})
export class FcpTreeSelectComponent implements ControlValueAccessor, AfterViewInit {
    readonly treeSelectFloatLabelVariant = FloatLabelVariant.In;

    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly destroyRef = inject(DestroyRef);
    @ViewChild(TreeSelect) private treeSelectRef?: TreeSelect;

    private readonly overlayContentWidthPx = signal<number | null>(null);
    private readonly overlayListHeightPx = signal<number | null>(null);

    constructor(@Optional() @Self() private readonly ngControl: NgControl | null) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }

        // appendTo defaults to 'body', so an already-open panel isn't covered by the
        // host's dimmed/pointer-events-none loading style — close it explicitly instead.
        effect(() => {
            if (this.loading()) {
                this.treeSelectRef?.hide();
            }
        });

        effect(() => {
            // Track options so the overlay resizes when tree data changes.
            this.options();
            const itemSize = this.virtualScrollItemSize();
            const scrollHeight = this.scrollHeight();
            untracked(() => {
                queueMicrotask(() => this.syncOverlaySize(this.getVisibleNodes(), itemSize, scrollHeight));
            });
        });
    }

    ngAfterViewInit(): void {
        // p-treeSelect v19 never wires its inner p-tree's onFilter back to its own onFilter emitter.
        // p-overlay uses *ngIf, so treeViewChild is destroyed/re-created on each open — re-subscribe every onShow.
        let filterSub: Subscription | undefined;
        this.destroyRef.onDestroy(() => filterSub?.unsubscribe());

        this.treeSelectRef?.onShow.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            filterSub?.unsubscribe();
            const filter$ = this.treeSelectRef?.treeViewChild?.onFilter;
            if (filter$) {
                filterSub = filter$.subscribe((event) => {
                    this.onFilter.emit(event);
                    queueMicrotask(() =>
                        this.syncOverlaySize(this.getVisibleNodes(), this.virtualScrollItemSize(), this.scrollHeight())
                    );
                });
            }
        });
    }

    e2e = input<string | null>(null);
    id = input<string | undefined>(undefined);
    placeholder = input<string>('');
    disabled = input<boolean>(false);
    options = input<TreeNode[]>([]);

    selectionMode = input<FcpTreeSelectSelectionMode>(FcpTreeSelectSelectionMode.Single);
    metaKeySelection = input<boolean>(true);
    propagateSelectionDown = input<boolean>(true);
    propagateSelectionUp = input<boolean>(true);

    filter = input<boolean>(false);
    filterPlaceholder = input<string>('');
    filterBy = input<string>('label');
    filterMode = input<string>('lenient');
    filterInputAutoFocus = input<boolean>(true);
    resetFilterOnHide = input<boolean>(false);

    showClear = input<boolean>(false);
    invalid = input<boolean>(false);
    variant = input<FcpTreeSelectVariant>(FcpTreeSelectVariant.Outlined);
    size = input<FcpTreeSelectSize>(FcpTreeSelectSize.Normal);

    loading = input<boolean>(false);

    virtualScroll = input<boolean>(false);
    virtualScrollItemSize = input<number>(46);
    virtualScrollOptions = input<ScrollerOptions | undefined>();

    scrollHeight = input<string>('400px');

    emptyMessage = input<string>('');
    panelStyle = input<Record<string, string | number>>({});
    panelStyleClass = input<string>('');

    floatLabel = input<boolean>(false);

    fluid = input<boolean>(false);
    appendTo = input<string | HTMLElement>('body');

    ariaLabel = input<string | undefined>(undefined);
    ariaLabelledBy = input<string | undefined>(undefined);

    icon = input<string | undefined>(undefined);

    readonly showFloatLabel = computed(() => this.floatLabel() && this.size() !== FcpTreeSelectSize.Small);
    readonly resolvedPanelStyleClass = computed(() => this.panelStyleClass().trim());
    readonly resolvedPanelStyle = computed(() => {
        const style: Record<string, string | number> = {
            ...this.panelStyle()
        };
        const contentWidth = this.overlayContentWidthPx();
        if (contentWidth != null) {
            style['--fcp-tree-select-content-width'] = `${contentWidth}px`;
        }
        const listHeight = this.overlayListHeightPx();
        if (listHeight != null) {
            style['--fcp-tree-select-list-height'] = `${listHeight}px`;
        }
        return style;
    });

    onChange = output<unknown>();
    onFocus = output<Event>();
    onHide = output<Event>();
    onShow = output<Event>();
    onClear = output<void>();
    onFilter = output<TreeFilterEvent>();
    onNodeExpand = output<TreeSelectNodeExpandEvent>();
    onNodeCollapse = output<TreeSelectNodeCollapseEvent>();
    onNodeSelect = output<TreeNodeSelectEvent>();
    onNodeUnselect = output<TreeNodeUnSelectEvent>();

    private _value = signal<unknown>(null);
    private _onChange = signal<(value: unknown) => void>(() => {});
    private _onTouched = signal<() => void>(() => {});
    private _formDisabled = signal<boolean>(false);

    modelValue = computed(() => this._value());
    isDisabled = computed(() => this.disabled() || this._formDisabled());

    primeTreeSelectSize = computed(() => (this.size() === FcpTreeSelectSize.Small ? 'small' : 'large'));

    writeValue(value: unknown): void {
        this._value.set(value);
    }

    registerOnChange(fn: (value: unknown) => void): void {
        this._onChange.set(fn);
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched.set(fn);
    }

    setDisabledState(isDisabled: boolean): void {
        this._formDisabled.set(isDisabled);
    }

    hide(): void {
        if (this.treeSelectRef?.overlayVisible) this.treeSelectRef.hide();
    }

    handleChange(value: unknown): void {
        this._value.set(value);
        this._onChange()(value);
        this.onChange.emit(value);
    }

    handleBlur(): void {
        this._onTouched()();
    }

    handlePanelShow(event: Event): void {
        const nodes = this.getVisibleNodes();
        if (this.countTreeNodes(nodes) === 0) {
            this.seedOverlayWidthFromTrigger();
            this.seedOverlayHeightForEmpty();
        } else {
            this.syncOverlaySize(nodes, this.virtualScrollItemSize(), this.scrollHeight());
        }
        this.onShow.emit(event);
    }

    handleFilter(event: TreeFilterEvent): void {
        this.onFilter.emit(event);
        queueMicrotask(() =>
            this.syncOverlaySize(this.getVisibleNodes(), this.virtualScrollItemSize(), this.scrollHeight())
        );
    }

    handleNodeExpand(event: TreeSelectNodeExpandEvent): void {
        this.onNodeExpand.emit(event);
        queueMicrotask(() =>
            this.syncOverlaySize(this.getVisibleNodes(), this.virtualScrollItemSize(), this.scrollHeight())
        );
    }

    handleNodeCollapse(event: TreeSelectNodeCollapseEvent): void {
        this.onNodeCollapse.emit(event);
        queueMicrotask(() =>
            this.syncOverlaySize(this.getVisibleNodes(), this.virtualScrollItemSize(), this.scrollHeight())
        );
    }

    protected get isControlInvalidAndDirty(): boolean {
        const control = this.ngControl?.control ?? null;
        return !!control && control.invalid && (control.dirty || control.touched);
    }

    private getVisibleNodes(): TreeNode[] {
        const filtered = this.treeSelectRef?.treeViewChild?.filteredNodes;
        if (filtered !== undefined && filtered !== null) {
            return filtered;
        }
        return this.options();
    }

    private syncOverlaySize(nodes: TreeNode[], itemSize: number, scrollHeight: string): void {
        if (typeof document === 'undefined') {
            return;
        }

        const optionCount = this.countTreeNodes(nodes);
        if (optionCount === 0) {
            this.seedOverlayHeightForEmpty();
            return;
        }

        const labels = this.collectTreeLabels(nodes);
        const triggerWidth = this.getTriggerWidth();
        const longestLabelWidth = this.measureLongestLabelWidth(labels);
        const contentWidth = longestLabelWidth + this.getOptionChromeWidth();
        const width = this.resolveOverlayWidth(triggerWidth, contentWidth);

        const maxListHeight = this.parseCssLengthPx(scrollHeight, 400);
        const listHeight = Math.min(maxListHeight, optionCount * itemSize);

        this.overlayContentWidthPx.set(Math.ceil(width));
        this.overlayListHeightPx.set(Math.ceil(listHeight));
        this.realignOpenOverlay();
    }

    private seedOverlayWidthFromTrigger(): void {
        if (typeof document === 'undefined') {
            return;
        }
        const triggerWidth = this.getTriggerWidth();
        if (triggerWidth <= 0) {
            return;
        }
        this.overlayContentWidthPx.set(Math.ceil(this.resolveOverlayWidth(triggerWidth, triggerWidth)));
        this.realignOpenOverlay();
    }

    /**
     * Match the trigger width. Grow for long labels up to 480px only when the trigger is
     * narrower than that cap. If a wider-than-trigger panel cannot fit left-aligned under
     * the field, match the trigger instead so DomHandler does not shift the overlay past
     * the field edges (padded small screens).
     */
    private resolveOverlayWidth(triggerWidth: number, contentWidth: number): number {
        // Cap applies to content-driven growth, not to matching a wider trigger.
        const width = Math.max(triggerWidth, Math.min(contentWidth, OVERLAY_MAX_WIDTH_PX));

        if (typeof window === 'undefined') {
            return width;
        }

        const triggerLeft = this.getTriggerElement()?.getBoundingClientRect().left ?? 0;
        const spaceRight = Math.max(0, window.innerWidth - triggerLeft);

        if (width > spaceRight) {
            return Math.min(triggerWidth, spaceRight);
        }
        return Math.min(width, spaceRight);
    }

    private seedOverlayHeightForEmpty(): void {
        this.overlayListHeightPx.set(this.virtualScrollItemSize());
        this.realignOpenOverlay();
    }

    /** Counts currently visible rows (expanded path only) for list height. */
    private countTreeNodes(nodes: TreeNode[]): number {
        let count = 0;
        for (const node of nodes) {
            count += 1;
            if (node.expanded && node.children?.length) {
                count += this.countTreeNodes(node.children);
            }
        }
        return count;
    }

    /** Labels for currently visible rows (expanded path only) for content width. */
    private collectTreeLabels(nodes: TreeNode[]): string[] {
        const labels: string[] = [];
        for (const node of nodes) {
            if (node.label) {
                labels.push(node.label);
            }
            if (node.expanded && node.children?.length) {
                labels.push(...this.collectTreeLabels(node.children));
            }
        }
        return labels;
    }

    private parseCssLengthPx(value: string, fallback: number): number {
        const trimmed = value.trim();
        const match = /^(-?\d*\.?\d+)(px|rem)?$/i.exec(trimmed);
        if (!match) {
            return fallback;
        }
        const amount = Number.parseFloat(match[1]);
        if (!Number.isFinite(amount)) {
            return fallback;
        }
        if ((match[2] ?? 'px').toLowerCase() === 'rem') {
            const rootFontSize =
                typeof document !== 'undefined'
                    ? Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
                    : 16;
            return amount * rootFontSize;
        }
        return amount;
    }

    private measureLongestLabelWidth(labels: string[]): number {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
            return 0;
        }

        const rootStyle = getComputedStyle(document.documentElement);
        const bodyStyle = getComputedStyle(document.body);
        const fontSize =
            rootStyle.getPropertyValue('--typography-body-m-size-regular').trim() || bodyStyle.fontSize || '14px';
        const fontWeight =
            rootStyle.getPropertyValue('--typography-body-m-weight-regular').trim() || bodyStyle.fontWeight || '400';
        const fontFamily = bodyStyle.fontFamily || 'sans-serif';
        context.font = `${fontWeight} ${fontSize} ${fontFamily}`;

        let longest = 0;
        for (const label of labels) {
            longest = Math.max(longest, context.measureText(label).width);
        }
        return longest;
    }

    /** Toggler + gap + node/tree horizontal padding (design tokens). */
    private getOptionChromeWidth(): number {
        const sizing = (token: string, fallback: number) => {
            const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
            const parsed = Number.parseFloat(raw);
            return Number.isFinite(parsed) ? parsed : fallback;
        };
        const toggler = sizing('--sizing-20', 20);
        const gap = sizing('--sizing-8', 8);
        const nodePaddingX = sizing('--sizing-12', 12) * 2;
        const treePaddingX = sizing('--sizing-12', 12) * 2;
        return toggler + gap + nodePaddingX + treePaddingX;
    }

    private getTriggerElement(): HTMLElement | null {
        return this.host.nativeElement.querySelector('.p-treeselect') as HTMLElement | null;
    }

    private getTriggerWidth(): number {
        return this.getTriggerElement()?.offsetWidth || this.host.nativeElement.offsetWidth || 0;
    }

    private realignOpenOverlay(): void {
        if (!this.treeSelectRef?.overlayVisible) {
            return;
        }
        requestAnimationFrame(() => {
            this.treeSelectRef?.overlayViewChild?.alignOverlay();
        });
    }
}

export enum FcpTreeSelectSelectionMode {
    Single = 'single',
    Checkbox = 'checkbox'
}

export enum FcpTreeSelectVariant {
    Outlined = 'outlined',
    Filled = 'filled'
}

export enum FcpTreeSelectSize {
    Normal = null,
    Small = 'small'
}

export type FcpTreeSelectNode = TreeNode;
